import React, { useState } from 'react';
import { niveauDuNom, ORDRE_NIVEAUX, ENSEMBLE } from '../../utils/releveDepuisDossier';

// Le bien vu étage par étage : pour chaque niveau, le plan de référence tel
// que photographié pendant la visite quand il existe, et ses pièces avec
// leurs dimensions. On clique une pièce pour la corriger en grand. Aucun
// prix ici : l'écran sert à valider la configuration du chantier — les
// montants viennent à l'étape Postes.
//
// Un relevé de seize pièces défilait en une seule liste de pastilles ; ici
// la maison se lit comme on la parcourt : sous-sol, rez-de-chaussée, étages.

export const SANS_NIVEAU = 'Niveau non précisé';
// « Ensemble du bien » n'est pas une pièce sans étage : c'est la corbeille des
// postes qui valent pour tout le bâtiment (chaudière, tableau, fenêtres comptées
// en bloc). Elle a sa section, en dernier, et on le dit.
export const SOUS_TITRES = {
  [ENSEMBLE]: 'Postes de tout le bâtiment, sans pièce : équipements, réseaux, quantités comptées en bloc par l\'analyse. Ses surfaces sont la somme des pièces mesurées : un poste au m² posé ici couvre tout le bien. À ventiler dans les pièces si besoin.',
  [SANS_NIVEAU]: 'Pièces citées mais non situées : choisissez l\'étage dans l\'en-tête de la pièce.',
};

export function niveauDe(room) {
  if ((room.customLabel || room.label) === ENSEMBLE) return ENSEMBLE;
  return (room.niveau && (niveauDuNom(room.niveau) || room.niveau)) || niveauDuNom(room.customLabel || room.label) || null;
}

/** [{ niveau, rooms }] dans l'ordre des étages ; les pièces sans niveau, puis l'ensemble du bien, en dernier. */
export function regrouperParNiveau(rooms) {
  const groupes = new Map();
  for (const r of rooms) {
    const n = niveauDe(r) || SANS_NIVEAU;
    (groupes.get(n) || groupes.set(n, []).get(n)).push(r);
  }
  const rang = (n) => { const i = ORDRE_NIVEAUX.indexOf(n); return i === -1 ? (n === ENSEMBLE ? 100 : n === SANS_NIVEAU ? 99 : 50) : i; };
  return [...groupes].map(([niveau, rs]) => ({ niveau, rooms: rs })).sort((a, b) => rang(a.niveau) - rang(b.niveau));
}

const fmt = (n) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n || 0);
const polyArea = (v) => { let a = 0; for (let i = 0; i < v.length; i++) { const j = (i + 1) % v.length; a += v[i].x * v[j].y - v[j].x * v[i].y; } return Math.abs(a) / 2; };
const bbox = (v) => { let a = Infinity, b = Infinity, c = -Infinity, d = -Infinity; for (const p of v) { a = Math.min(a, p.x); b = Math.min(b, p.y); c = Math.max(c, p.x); d = Math.max(d, p.y); } return { minX: a, minY: b, w: c - a, h: d - b }; };

// Le contour de la pièce, en petit : assez pour reconnaître sa forme et lire sa surface.
export function MiniPlan({ vertices, height, actif }) {
  if (!vertices || vertices.length < 3) {
    return <div className="h-20 rounded-md bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-[11px] text-slate-400">contour à tracer</div>;
  }
  const W = 160, H = 80, pad = 8;
  const bb = bbox(vertices);
  const k = Math.min((W - pad * 2) / Math.max(bb.w, 0.1), (H - pad * 2) / Math.max(bb.h, 0.1));
  const ox = (W - bb.w * k) / 2 - bb.minX * k, oy = (H - bb.h * k) / 2 - bb.minY * k;
  const d = vertices.map((v, i) => `${i ? 'L' : 'M'}${(v.x * k + ox).toFixed(1)},${(v.y * k + oy).toFixed(1)}`).join(' ') + ' Z';
  const aire = polyArea(vertices);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20 rounded-md bg-slate-50 border border-slate-100">
      <path d={d} fill={actif ? '#ffedd5' : '#fff7ed'} stroke="#ea580c" strokeWidth="1.5" strokeLinejoin="round" />
      <text x={W / 2} y={H / 2 + 4} textAnchor="middle" fill="#9a3412" fontSize="12" fontWeight="800">{aire.toFixed(1)} m²</text>
      {height && <text x={W - 6} y={H - 5} textAnchor="end" fill="#94a3b8" fontSize="8">h {Number(height).toFixed(2)} m</text>}
    </svg>
  );
}

// Dimensions d'un rectangle relevé (L × l) ; un contour libre n'a que sa surface.
function dims(room) {
  const v = room.vertices || [];
  if (v.length !== 4) return null;
  const L = Math.hypot(v[1].x - v[0].x, v[1].y - v[0].y), l = Math.hypot(v[2].x - v[1].x, v[2].y - v[1].y);
  return `${L.toFixed(2)} × ${l.toFixed(2)} m`;
}

/**
 * @param {object} p
 * @param {Array} p.rooms
 * @param {object} p.estimate       { rooms: [{ id, total, surface }] }
 * @param {Array} p.plans           photos de plans du dossier : [{ id, url, niveau, nom }]
 * @param {Function} p.emoji        room → pictogramme
 * @param {Function} p.onSelect     room.id
 * @param {Function} p.onAddRoom    (typeId, niveau)
 * @param {Array} p.roomTypes
 */
export default function PlanNiveaux({ rooms, estimate, plans = [], emoji, activeRoomId, onSelect, onAddRoom, roomTypes }) {
  const [zoom, setZoom] = useState(null);   // photo de plan en pleine lumière
  const groupes = regrouperParNiveau(rooms);
  const surfaceDe = (id) => estimate?.rooms?.find(r => r.id === id)?.surface || 0;
  const plansDe = (niveau) => plans.filter(p => (niveauDuNom(p.niveau || '') || p.niveau) === niveau);
  const plansSansNiveau = plans.filter(p => !(niveauDuNom(p.niveau || '') || p.niveau));

  return (
    <div className="space-y-3">
      {groupes.map(({ niveau, rooms: rs }) => {
        const refs = niveau === SANS_NIVEAU ? plansSansNiveau : plansDe(niveau);
        const surface = rs.filter(r => !r.technique).reduce((s, r) => s + surfaceDe(r.id), 0);   // une pièce technique n a pas de surface
        return (
          <section key={niveau} className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3">
              <h3 className="text-base font-black text-slate-900">
                {niveau}
                {niveau !== ENSEMBLE && <span className="ml-2 text-xs font-semibold text-slate-400">{rs.length} pièce{rs.length > 1 ? 's' : ''} · {surface.toFixed(1)} m²</span>}
              </h3>
              <div className="flex items-center gap-3">
                <select value="" onChange={e => { if (e.target.value) onAddRoom(e.target.value, niveau === SANS_NIVEAU || niveau === ENSEMBLE ? null : niveau); }}
                        className="select-compact sombre px-2 py-1 bg-slate-900 text-white rounded-lg text-xs font-bold outline-none cursor-pointer">
                  <option value="" disabled>+ Pièce</option>
                  {roomTypes.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
                </select>
              </div>
            </div>

            {SOUS_TITRES[niveau] && <p className="text-xs text-slate-500 -mt-2 mb-3">{SOUS_TITRES[niveau]}</p>}
            <div className={`grid gap-3 ${refs.length ? 'lg:grid-cols-[220px_1fr]' : ''}`}>
              {refs.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Plan de référence</p>
                  <div className="flex lg:flex-col gap-2">
                    {refs.map(p => (
                      <button key={p.id} type="button" onClick={() => setZoom(p)} title={p.nom}
                              className="block w-40 lg:w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-orange-400">
                        <img src={p.url} alt={p.nom || 'plan'} loading="lazy" className="w-full h-32 object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
                {rs.map(room => {
                  const actif = room.id === activeRoomId;
                  const nom = room.customLabel || room.label;
                  if (niveau === ENSEMBLE) {
                    // Pas de contour à corriger : la somme des pièces, telle que la page la calcule.
                    return (
                      <div key={room.id} className="col-span-2 sm:col-span-3 xl:col-span-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                        <span className="text-xs font-semibold text-slate-800">{emoji(room)} {nom}</span>
                        <span className="text-sm font-black text-orange-700">{surfaceDe(room.id).toFixed(1)} m² au sol</span>
                        <span className="text-[11px] text-slate-500">somme des pièces mesurées — murs et périmètre cumulés de même</span>
                        <span className="text-[11px] text-slate-400">{room.lines?.length || 0} poste{room.lines?.length > 1 ? 's' : ''}</span>
                      </div>
                    );
                  }
                  return (
                    <button key={room.id} type="button" onClick={() => onSelect(room.id)} title={nom}
                            className={`text-left rounded-lg border p-2 transition-colors ${actif ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500' : 'border-slate-200 bg-white hover:border-orange-300'}`}>
                      <div className="flex items-center gap-1.5 min-w-0 mb-1.5">
                        <span className="text-base leading-none shrink-0">{emoji(room)}</span>
                        <span className="text-xs font-semibold text-slate-800 truncate">{nom}</span>
                      </div>
                      <MiniPlan vertices={room.vertices} height={room.height} actif={actif} />
                      <div className="flex items-baseline justify-between gap-2 mt-1.5">
                        <span className="text-[11px] text-slate-500 truncate">{dims(room) || (room.vertices?.length ? 'contour libre' : '—')}</span>
                        <span className="text-[11px] text-slate-400 shrink-0">h {Number(room.height || 0).toFixed(2)} m</span>
                      </div>
                      {(room.photos?.length > 0 || room.lines?.length > 0) && (
                        <div className="text-[10px] text-slate-400 mt-0.5">{room.lines?.length || 0} poste{room.lines?.length > 1 ? 's' : ''}{room.photos?.length ? ` · ${room.photos.length} photo${room.photos.length > 1 ? 's' : ''}` : ''}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {zoom && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setZoom(null)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl" aria-label="Fermer" onClick={() => setZoom(null)}>✕</button>
          <figure className="max-w-[92vw] max-h-[90vh] text-center" onClick={e => e.stopPropagation()}>
            <img src={zoom.url} alt="" className="max-w-[92vw] max-h-[82vh] object-contain rounded-lg" />
            <figcaption className="text-white/80 text-sm mt-2">{zoom.niveau ? `Plan — ${zoom.niveau}` : 'Plan'}{zoom.nom ? ` · ${zoom.nom}` : ''}</figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}

// Les pastilles de pièces, rangées par niveau : pour les écrans Postes et
// Style, où l'on travaille pièce par pièce.
export function PillsNiveaux({ rooms, estimate, emoji, activeRoomId, onSelect, onAddRoom, roomTypes, marque }) {
  const groupes = regrouperParNiveau(rooms);
  const totalDe = (id) => estimate?.rooms?.find(r => r.id === id)?.total || 0;
  return (
    <div className="space-y-2">
      {groupes.map(({ niveau, rooms: rs }) => (
        <div key={niveau} className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 w-full sm:w-auto sm:min-w-[7.5rem]">
            {niveau}
            <span className="normal-case tracking-normal font-normal"> · {fmt(rs.reduce((s, r) => s + totalDe(r.id), 0))} €</span>
          </span>
          {rs.map(room => {
            const actif = room.id === activeRoomId;
            const nom = room.customLabel || room.label;
            return (
              <button key={room.id} type="button" onClick={() => onSelect(room.id)} title={nom}
                      className={`flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-lg text-xs transition-colors max-w-full ${actif ? 'bg-orange-600 text-white shadow-sm' : 'bg-white text-slate-700 border border-slate-200 hover:border-orange-300'}`}>
                <span className="text-sm leading-none">{emoji(room)}</span>
                <span className="font-semibold truncate max-w-[11rem]">{nom}</span>
                <span className={`text-[11px] tabular-nums ${actif ? 'text-orange-100' : 'text-slate-400'}`}>{fmt(totalDe(room.id))} €</span>
                {marque && marque(room)}
              </button>
            );
          })}
          {onAddRoom && (
            <select value="" onChange={e => { if (e.target.value) onAddRoom(e.target.value, niveau === SANS_NIVEAU || niveau === ENSEMBLE ? null : niveau); }}
                    className="select-compact px-2 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold outline-none cursor-pointer">
              <option value="" disabled>+</option>
              {roomTypes.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
            </select>
          )}
        </div>
      ))}
    </div>
  );
}
